/**
 * Detalhe do grupo.
 *
 * Serve dois públicos na mesma tela: o participante, que quer ver a próxima
 * pelada e sua situação; e o administrador, que precisa gerenciar. O painel de
 * administração não é escondido por opacidade — simplesmente não é renderizado
 * para quem não tem o papel.
 */

import type { RankingMetric } from '@nossorachao/types';
import { formatCurrency, weekdayLabel } from '@nossorachao/utils';
import {
  Calendar,
  Copy,
  Hand,
  MapPin,
  Plus,
  Settings,
  Shuffle,
  Ticket,
  TrendingDown,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { GameCard } from '@/components/game-card';
import { PageHeader } from '@/components/layout';
import {
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  PositionBadge,
  Skeleton,
  StarRating,
  StatTile,
  Tabs,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import {
  useAdminDashboard,
  useChangeMemberRole,
  useCreateGame,
  useCreateInvite,
  useGames,
  useGroup,
  useMembers,
  useMyRating,
  useRanking,
  useTransferOwnership,
  useUpdateMemberSkill,
} from '@/lib/queries';
import { useAuth } from '@/store/auth';

import { GroupFinancial } from './financial';
import { BillingBanner, GroupBilling } from './group-billing';
import { Modal } from './groups';

type Tab = 'games' | 'members' | 'financial' | 'ranking' | 'history' | 'billing';

/**
 * Métricas do ranking. "Nível" só entra para administrador: a nota é dado de
 * administração neste produto, e um ranking por nota é a nota exposta com
 * outro nome — a API recusa o pedido, então nem oferecemos a aba.
 */
const METRICS: Array<{ key: RankingMetric; label: string; adminOnly?: boolean }> = [
  { key: 'SKILL', label: 'Nível', adminOnly: true },
  { key: 'GAMES_PLAYED', label: 'Jogos' },
  { key: 'WINS', label: 'Vitórias' },
  { key: 'ATTENDANCE', label: 'Presença' },
  { key: 'GOALS', label: 'Gols' },
];

const TAB_TO_PARAM: Record<Tab, string> = {
  games: 'partidas',
  members: 'participantes',
  financial: 'financeiro',
  ranking: 'ranking',
  history: 'historico',
  billing: 'assinatura',
};

const TAB_PARAM_TO_TAB: Record<string, Tab> = Object.fromEntries(
  Object.entries(TAB_TO_PARAM).map(([tab, param]) => [param, tab as Tab]),
);

export function GroupDetailPage() {
  const { groupId = '' } = useParams();
  const navigate = useNavigate();

  const group = useGroup(groupId);
  const members = useMembers(groupId);
  const upcoming = useGames(groupId, 'upcoming');
  const past = useGames(groupId, 'past');
  const myRating = useMyRating(groupId);

  const isAdmin = group.data ? group.data.myRole !== 'MEMBER' : false;
  const admin = useAdminDashboard(groupId, isAdmin);

  // A aba vive na URL (?aba=financeiro) para dar para linkar e voltar nela.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (TAB_PARAM_TO_TAB[searchParams.get('aba') ?? ''] ?? 'games') as Tab;
  const setTab = (next: Tab) =>
    setSearchParams(next === 'games' ? {} : { aba: TAB_TO_PARAM[next] }, { replace: true });
  // Começa em "Jogos", que todo mundo pode ver — "Nível" seria 403 na primeira
  // renderização para participante comum.
  const [metric, setMetric] = useState<RankingMetric>('GAMES_PLAYED');
  const ranking = useRanking(groupId, metric);

  const [modal, setModal] = useState<'none' | 'game' | 'invite'>('none');
  const myUserId = useAuth((state) => state.user?.id);
  const isOwner = group.data?.myRole === 'OWNER';
  const [roleTarget, setRoleTarget] = useState<{
    memberId: string;
    name: string;
    role: 'OWNER' | 'ADMIN' | 'MEMBER';
  } | null>(null);
  const [skillTarget, setSkillTarget] = useState<{
    memberId: string;
    name: string;
    skillRating: number;
  } | null>(null);

  if (group.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!group.data) {
    return (
      <Card>
        <EmptyState
          icon={Users}
          title="Grupo não encontrado"
          description="Ele pode ter sido excluído, ou você não participa mais dele."
          action={<Button onClick={() => navigate('/grupos')}>Ver minhas peladas</Button>}
        />
      </Card>
    );
  }

  const detail = group.data;

  return (
    <>
      <PageHeader
        title={detail.name}
        subtitle={detail.description ?? undefined}
        back={{ to: '/grupos', label: 'Peladas' }}
        action={
          isAdmin ? (
            <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap">
              <Button
                variant="secondary"
                icon={Settings}
                onClick={() => navigate(`/grupos/${groupId}/editar`)}
              >
                Editar
              </Button>
              <Button variant="secondary" icon={Ticket} onClick={() => setModal('invite')}>
                Convidar
              </Button>
              <Button icon={Plus} className="col-span-2" onClick={() => setModal('game')}>
                Nova partida
              </Button>
            </div>
          ) : null
        }
      />

      <BillingBanner groupId={groupId} isAdmin={isAdmin} onOpen={() => setTab('billing')} />

      {/* Identidade */}
      <Card className="mb-6">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-ink-2">
          <span className="flex items-center gap-1.5">
            <Calendar className="h-4 w-4 text-ink-3" aria-hidden />
            {weekdayLabel(detail.weekday)} · {detail.startTime}
            {detail.endTime ? ` – ${detail.endTime}` : ''}
          </span>
          <span className="flex items-center gap-1.5">
            <Users className="h-4 w-4 text-ink-3" aria-hidden />
            {detail.memberCount} participantes
          </span>
          {detail.venueName ? (
            <span className="flex items-center gap-1.5">
              <MapPin className="h-4 w-4 text-ink-3" aria-hidden />
              {detail.venueName}
            </span>
          ) : null}
          {detail.monthlyFeeCents > 0 ? (
            <span className="flex items-center gap-1.5">
              <Wallet className="h-4 w-4 text-ink-3" aria-hidden />
              {formatCurrency(detail.monthlyFeeCents)} · vence dia {detail.dueDayOfMonth}
            </span>
          ) : null}
        </div>
      </Card>

      {/* Minha situação */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {/*
          Para quem não administra, o card mostra a tendência sem o número: a
          nota é dado de administração, e "você está evoluindo" é o feedback
          que interessa ao jogador sem expor um 1,8 na tela dele.
        */}
        <StatTile
          label={myRating.data?.currentSkill !== null ? 'Meu nível' : 'Minha evolução'}
          value={
            myRating.data?.currentSkill != null
              ? myRating.data.currentSkill.toFixed(1).replace('.', ',')
              : myRating.data?.trend === 'UP'
                ? 'Subindo'
                : myRating.data?.trend === 'DOWN'
                  ? 'Caindo'
                  : 'Estável'
          }
          hint={
            myRating.data?.currentSkill != null
              ? myRating.data.trend === 'UP'
                ? 'subindo'
                : myRating.data.trend === 'DOWN'
                  ? 'caindo'
                  : 'estável'
              : `${myRating.data?.ratingsCount ?? 0} avaliações recebidas`
          }
          icon={
            myRating.data?.trend === 'UP'
              ? TrendingUp
              : myRating.data?.trend === 'DOWN'
                ? TrendingDown
                : undefined
          }
        />

        {isAdmin && admin.data ? (
          <>
            <StatTile
              label="Caixa"
              onClick={() => setTab('financial')}
              value={formatCurrency(admin.data.financial.balanceCents)}
              tone={admin.data.financial.balanceCents >= 0 ? 'ok' : 'bad'}
              icon={Wallet}
            />
            <StatTile
              label="Mensalidades"
              onClick={() => setTab('financial')}
              value={String(admin.data.fees.pending + admin.data.fees.overdue)}
              hint={`${admin.data.fees.paid} pagas · ver mensalidades`}
              tone={admin.data.fees.overdue > 0 ? 'bad' : 'warn'}
            />
            <StatTile
              label="Participantes"
              onClick={() => setTab('members')}
              value={String(admin.data.members.active)}
              hint={
                admin.data.members.pending > 0
                  ? `${admin.data.members.pending} aguardando`
                  : undefined
              }
              tone={admin.data.members.pending > 0 ? 'warn' : 'default'}
              icon={Users}
            />
          </>
        ) : null}
      </div>

      {/* Ação rápida do admin */}
      {isAdmin && admin.data?.nextGame && !admin.data.nextGame.teamsGenerated ? (
        <Card accent="rgb(18 183 106)" className="mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold">Times ainda não sorteados</p>
              <p className="text-[13px] text-ink-2">
                {admin.data.nextGame.confirmedCount} de {admin.data.nextGame.maxPlayers} confirmados
                para a próxima partida.
              </p>
            </div>
            <Button
              icon={Shuffle}
              onClick={() => navigate(`/partidas/${admin.data!.nextGame!.id}/times`)}
            >
              Sortear times
            </Button>
          </div>
        </Card>
      ) : null}

      <div className="mb-4">
        <Tabs<Tab>
          active={tab}
          onChange={setTab}
          tabs={[
            { key: 'games', label: 'Partidas', count: upcoming.data?.data.length },
            { key: 'members', label: 'Participantes', count: detail.memberCount },
            { key: 'financial', label: 'Financeiro' },
            { key: 'ranking', label: 'Ranking' },
            { key: 'history', label: 'Histórico', count: past.data?.data.length },
            // A assinatura é assunto da administração; participante só vê o
            // aviso de somente leitura, se houver.
            ...(isAdmin ? [{ key: 'billing' as const, label: 'Assinatura' }] : []),
          ]}
        />
      </div>

      {tab === 'games' ? (
        <div className="space-y-4">
          {!upcoming.data?.data.length ? (
            <Card>
              <EmptyState
                icon={Calendar}
                title="Nenhuma partida marcada"
                description={
                  isAdmin
                    ? 'Crie a próxima pelada para abrir a lista de presença.'
                    : 'Assim que o administrador marcar, você recebe um aviso.'
                }
                action={
                  isAdmin ? (
                    <Button icon={Plus} onClick={() => setModal('game')}>
                      Criar partida
                    </Button>
                  ) : undefined
                }
              />
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {upcoming.data.data.map((game) => (
                <GameCard key={game.id} game={game} />
              ))}
            </div>
          )}

          {detail.rules ? (
            <Card>
              <h3 className="mb-2 font-bold">Regras do grupo</h3>
              <p className="whitespace-pre-line text-[13px] leading-relaxed text-ink-2">
                {detail.rules}
              </p>
            </Card>
          ) : null}
        </div>
      ) : null}

      {tab === 'members' && members.isLoading ? <Skeleton className="h-48 w-full" /> : null}

      {tab === 'members' && members.error ? (
        <Card>
          <p className="text-sm text-bad">
            Não foi possível carregar os participantes
            {members.error instanceof ApiError ? `: ${members.error.message}` : '.'}
          </p>
        </Card>
      ) : null}

      {tab === 'members' && members.data ? (
        <Card className="p-0">
          <ul className="divide-y divide-line">
            {members.data?.map((member) => (
              <li key={member.id} className="flex items-center gap-3 p-4">
                <Avatar
                  name={member.user.name}
                  src={member.user.avatarUrl}
                  size={38}
                  badge={member.position === 'GOALKEEPER' ? Hand : undefined}
                />

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-sm font-semibold">
                      {member.nickname ?? member.user.name}
                    </p>
                    <PositionBadge
                      position={member.position}
                      canPlayGoalkeeper={member.canPlayGoalkeeper}
                      short
                    />
                    {member.role !== 'MEMBER' ? (
                      <Badge tone="brand">{member.role === 'OWNER' ? 'Dono' : 'Admin'}</Badge>
                    ) : null}
                    {member.status === 'PENDING' ? <Badge tone="warn">Aguardando</Badge> : null}
                  </div>
                  <p className="mt-0.5 text-[13px] text-ink-3">
                    {member.gamesPlayed} jogos · {member.attendanceRate}% presença
                  </p>
                </div>

                <div className="flex flex-col items-end gap-1">
                  {/* A API só devolve a nota para administrador; para o
                      participante comum ela chega nula e nada aparece. */}
                  {isAdmin && member.skillRating !== null ? (
                    <button
                      type="button"
                      title="Editar nota"
                      className="rounded-md px-1 transition-colors hover:bg-sunken"
                      onClick={() =>
                        setSkillTarget({
                          memberId: member.id,
                          name: member.nickname ?? member.user.name,
                          skillRating: member.skillRating!,
                        })
                      }
                    >
                      <StarRating value={member.skillRating} size={13} />
                    </button>
                  ) : null}
                  {/* Sobre o dono e sobre si mesmo não há o que fazer aqui; rebaixar
                      admin é só do dono, então para admin a ação só aparece em
                      participante comum. */}
                  {isAdmin &&
                  member.user.id !== myUserId &&
                  member.role !== 'OWNER' &&
                  member.status === 'ACTIVE' &&
                  (isOwner || member.role === 'MEMBER') ? (
                    <button
                      type="button"
                      className="text-[12px] font-semibold text-brand hover:underline"
                      onClick={() =>
                        setRoleTarget({
                          memberId: member.id,
                          name: member.nickname ?? member.user.name,
                          role: member.role,
                        })
                      }
                    >
                      Gerenciar papel
                    </button>
                  ) : null}
                  {member.feeStatus ? (
                    <Badge
                      tone={
                        member.feeStatus === 'PAID'
                          ? 'ok'
                          : member.feeStatus === 'OVERDUE'
                            ? 'bad'
                            : 'warn'
                      }
                    >
                      {member.feeStatus === 'PAID'
                        ? 'Pago'
                        : member.feeStatus === 'OVERDUE'
                          ? 'Atrasado'
                          : 'Pendente'}
                    </Badge>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {tab === 'financial' ? <GroupFinancial groupId={groupId} isAdmin={isAdmin} /> : null}

      {tab === 'billing' && isAdmin ? <GroupBilling groupId={groupId} /> : null}

      {tab === 'ranking' ? (
        <div className="space-y-4">
          <Tabs<RankingMetric>
            active={metric}
            onChange={setMetric}
            tabs={METRICS.filter((entry) => isAdmin || !entry.adminOnly).map((entry) => ({
              key: entry.key,
              label: entry.label,
            }))}
          />

          <Card className="p-0">
            {ranking.isLoading ? (
              <div className="p-4">
                <Skeleton className="h-10 w-full" />
              </div>
            ) : !ranking.data?.length ? (
              <p className="p-6 text-center text-sm text-ink-3">
                Ranking indisponível neste grupo.
              </p>
            ) : (
              <ul className="divide-y divide-line">
                {ranking.data.slice(0, 20).map((entry) => (
                  <li key={entry.member.id} className="flex items-center gap-3 p-4">
                    <span
                      className={`tabular w-8 text-sm font-bold ${
                        entry.position <= 3 ? 'text-brand' : 'text-ink-3'
                      }`}
                    >
                      {entry.position}º
                    </span>

                    <Avatar
                      name={entry.member.user.name}
                      src={entry.member.user.avatarUrl}
                      size={32}
                    />

                    <span className="flex-1 truncate text-sm">
                      {entry.member.nickname ?? entry.member.user.name}
                    </span>

                    <span className="tabular font-bold">
                      {metric === 'SKILL'
                        ? entry.value.toFixed(1).replace('.', ',')
                        : metric === 'ATTENDANCE'
                          ? `${entry.value}%`
                          : entry.value}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      ) : null}

      {tab === 'history' ? (
        !past.data?.data.length ? (
          <Card>
            <EmptyState
              icon={Calendar}
              title="Sem partidas anteriores"
              description="O histórico aparece aqui depois da primeira pelada."
            />
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {past.data.data.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </div>
        )
      ) : null}

      {modal === 'game' ? (
        <CreateGameModal groupId={groupId} onClose={() => setModal('none')} />
      ) : null}
      {modal === 'invite' ? (
        <InviteModal groupId={groupId} onClose={() => setModal('none')} />
      ) : null}

      {isAdmin && roleTarget ? (
        <RoleModal
          groupId={groupId}
          target={roleTarget}
          isOwner={isOwner}
          onClose={() => setRoleTarget(null)}
        />
      ) : null}

      {isAdmin && skillTarget ? (
        <SkillModal groupId={groupId} target={skillTarget} onClose={() => setSkillTarget(null)} />
      ) : null}
    </>
  );
}

/**
 * Papéis no grupo.
 *
 * Qualquer administrador promove; só o dono rebaixa e transfere a
 * propriedade. Existe um único dono por grupo — transferir rebaixa o atual
 * para administrador, e por ser difícil de desfazer a API pede a senha.
 */
function RoleModal({
  groupId,
  target,
  isOwner,
  onClose,
}: {
  groupId: string;
  target: { memberId: string; name: string; role: 'OWNER' | 'ADMIN' | 'MEMBER' };
  isOwner: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const changeRole = useChangeMemberRole(groupId);
  const transfer = useTransferOwnership(groupId);
  const [confirmTransfer, setConfirmTransfer] = useState(false);
  const [password, setPassword] = useState('');

  const error = changeRole.error ?? transfer.error;

  return (
    <Modal title={target.name} onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-ink-2">
          Papel atual: <strong>{target.role === 'ADMIN' ? 'Administrador' : 'Participante'}</strong>
        </p>

        {!confirmTransfer ? (
          <div className="space-y-2">
            {target.role === 'MEMBER' ? (
              <Button
                full
                loading={changeRole.isPending}
                onClick={() =>
                  changeRole.mutate(
                    { memberId: target.memberId, role: 'ADMIN' },
                    { onSuccess: onClose },
                  )
                }
              >
                Tornar administrador
              </Button>
            ) : null}

            {target.role === 'ADMIN' && isOwner ? (
              <Button
                full
                variant="secondary"
                loading={changeRole.isPending}
                onClick={() =>
                  changeRole.mutate(
                    { memberId: target.memberId, role: 'MEMBER' },
                    { onSuccess: onClose },
                  )
                }
              >
                Remover administrador
              </Button>
            ) : null}

            {isOwner ? (
              <Button full variant="danger" onClick={() => setConfirmTransfer(true)}>
                Tornar dono da pelada
              </Button>
            ) : (
              <p className="text-[13px] text-ink-3">
                Remover administradores e trocar o dono são ações exclusivas do dono da pelada.
              </p>
            )}
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              transfer.mutate(
                { newOwnerMemberId: target.memberId, password },
                {
                  onSuccess: () => {
                    onClose();
                    navigate(`/grupos/${groupId}`);
                  },
                },
              );
            }}
          >
            <p className="text-sm text-ink-2">
              <strong>{target.name}</strong> passa a ser o dono e você vira administrador. Só o novo
              dono poderá desfazer isso.
            </p>
            <Field label="Confirme sua senha">
              <Input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoFocus
              />
            </Field>
            <div className="flex gap-3">
              <Button variant="secondary" full onClick={() => setConfirmTransfer(false)}>
                Voltar
              </Button>
              <Button
                type="submit"
                variant="danger"
                full
                loading={transfer.isPending}
                disabled={!password}
              >
                Transferir
              </Button>
            </div>
          </form>
        )}

        {error ? (
          <p className="text-[12px] text-bad">
            {error instanceof ApiError ? error.message : 'Não foi possível alterar o papel.'}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * Nota manual do admin.
 *
 * Grava um override que congela a nota automática das avaliações. A
 * justificativa é exigida pela API e vai para a auditoria — a nota mexe no
 * sorteio dos times, então mudar sem motivo precisa deixar rastro.
 */
function SkillModal({
  groupId,
  target,
  onClose,
}: {
  groupId: string;
  target: { memberId: string; name: string; skillRating: number };
  onClose: () => void;
}) {
  const update = useUpdateMemberSkill(groupId);
  // A nota calculada tem duas casas; o override aceita só meia estrela.
  const [value, setValue] = useState(
    Math.min(5, Math.max(1, Math.round(target.skillRating * 2) / 2)),
  );
  const [reason, setReason] = useState('');

  return (
    <Modal title={`Nota de ${target.name}`} onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-ink-2">
          Só administradores veem a nota. Ela é usada para equilibrar os times e, depois de definida
          aqui, deixa de ser recalculada pelas avaliações.
        </p>

        <div className="flex justify-center py-2">
          <StarRating
            value={value}
            size={30}
            interactive
            onChange={(next) => setValue(Math.max(1, next))}
          />
        </div>

        <Field
          label="Motivo"
          error={update.error instanceof ApiError ? update.error.message : undefined}
        >
          <Input
            value={reason}
            maxLength={200}
            placeholder="Ex.: Evoluiu bastante nos últimos jogos"
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>

        <div className="flex gap-3">
          <Button variant="secondary" full onClick={onClose}>
            Cancelar
          </Button>
          <Button
            full
            loading={update.isPending}
            disabled={reason.trim().length < 3}
            onClick={() =>
              update.mutate(
                { memberId: target.memberId, skillRating: value, reason: reason.trim() },
                { onSuccess: onClose },
              )
            }
          >
            Salvar nota
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function CreateGameModal({ groupId, onClose }: { groupId: string; onClose: () => void }) {
  const create = useCreateGame(groupId);
  const [datetime, setDatetime] = useState('');
  const [notes, setNotes] = useState('');

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    try {
      await create.mutateAsync({
        scheduledAt: new Date(datetime).toISOString(),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      onClose();
    } catch {
      // O erro aparece abaixo do campo.
    }
  }

  return (
    <Modal title="Nova partida" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field
          label="Data e horário"
          error={create.error instanceof ApiError ? create.error.message : undefined}
        >
          <Input
            type="datetime-local"
            value={datetime}
            onChange={(event) => setDatetime(event.target.value)}
            invalid={Boolean(create.error)}
            required
            autoFocus
          />
        </Field>

        <Field label="Observações" hint="Opcional — aparece para todos">
          <Input
            placeholder="Ex.: levar colete claro"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>

        <div className="flex gap-3">
          <Button variant="secondary" full onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" full loading={create.isPending} disabled={!datetime}>
            Criar e avisar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Geração de convite.
 *
 * O código aparece uma única vez: o banco guarda só o hash, então nem o
 * servidor consegue mostrá-lo de novo depois. A tela diz isso em vez de deixar
 * o admin descobrir sozinho.
 */
function InviteModal({ groupId, onClose }: { groupId: string; onClose: () => void }) {
  const create = useCreateInvite(groupId);
  const [copied, setCopied] = useState(false);

  const inviteUrl = create.data
    ? (create.data.url ?? `${window.location.origin}/convite/${create.data.code}`)
    : null;

  async function copy() {
    if (!inviteUrl) return;
    await navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Modal title="Convidar para a pelada" onClose={onClose}>
      {!inviteUrl ? (
        <div className="space-y-4">
          <p className="text-sm text-ink-2">
            O link vale por 7 dias. Depois de gerado, ele aparece uma única vez — o servidor guarda
            apenas o hash.
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" full onClick={onClose}>
              Cancelar
            </Button>
            <Button full loading={create.isPending} onClick={() => create.mutate(7)}>
              Gerar link
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <Field label="Link do convite" hint="Copie agora — ele não será exibido de novo">
            <div className="flex gap-2">
              <Input readOnly value={inviteUrl} className="font-mono text-[12px]" />
              <Button variant="secondary" icon={Copy} onClick={copy}>
                {copied ? 'Copiado' : 'Copiar'}
              </Button>
            </div>
          </Field>

          <div className="flex gap-2">
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`Bora jogar? Entra na pelada: ${inviteUrl}`)}`}
              target="_blank"
              rel="noreferrer"
              className="flex-1"
            >
              <Button variant="secondary" full>
                WhatsApp
              </Button>
            </a>
            <Button full onClick={onClose}>
              Concluir
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
